import { useCallback, useEffect, useState } from 'react';

import type { JoinRequest, MemberRow, VoicePart } from '@contracts/index';

import { ApiError, api } from '../api';
import { missesInARow, monthOf, plural, relativeDay, voicePartName } from '../format';
import { ChevronDownIcon, ChevronRightIcon, DotsIcon } from '../icons';
import useLeaving from '../leaving';
import Avatar, { AVATAR_ROW, smileOf } from './Avatar';
import { livenessPhase, useGestures, type LiveFace } from './faceLiveness';
import PartSegments, { VOICE_PARTS as PARTS } from './PartSegments';
import SexSegments from './SexSegments';
import Sheet from './Sheet';
import { Empty, Failed, Settling, Skeleton } from './States';
import type { Report } from './UndoBar';
import Unfold from './Unfold';

/**
 * **The roster card, from `legacy/frontend/src/screens/MembersCard.tsx` — a region port, built
 * up task by task** (`docs/specs/features/F-016/plan.md` T8, T9).
 *
 * What is here is the whole of the roster as this slice draws it. T8 brought the roster's line
 * and the tenure under it (`:44-99`), the doc comment and the props the card is given
 * (`:101-146`), its own request (`:207-217`, `:233-235`), what is counted and grouped (`:345`,
 * `:357-386`), `Состав · N`, the skeleton and the failure (`:500-510`), and the sections with
 * their rows (`:550-630`). T9 brought the rest: `awayLine` (`:28-42`), `readOnly` and
 * `supportContact` with their defaults (`:120-121`), `pausedOpen` (`:157-158`), `me`, `isHead`
 * and `canManage` (`:346-356`), `away` (`:362`), `alone` (`:396-401`), the empty choir
 * (`:512-519`), the head's and the deputy's hints (`:522-548`), the sections' `!alone &&`
 * (`:556`), and the misses hint, the second-regent hint and «В паузе» (`:632-711`).
 * F-018 (`docs/specs/features/F-018/plan.md`, spec §0.2) brought back the rest of the old card.
 * T4 brought `requests` (`:150-151`), `knock` and its effect (`:219-231`, `:388-394`) and
 * `alone`'s conjunct (`:401`). T5 brought `knockedAt` (`:16-26`), `knocking` (`:152-156`),
 * `moreAt` (`:196-201`) and the request block (`:405-498`), with three seams in the block. T6
 * brought **`onDone`** (`:123`, `:147`) and its one call, `answer()` with its comment
 * (`:279-312`), together (F-017 spec §0.2, the critic's cut 2), and closed the seams of «Принять»
 * and «Нет» (`:445`, `:452`). T7 brought `linking`, `replacing` and `typedLink` (`:176-195`),
 * `link()` (`:314-343`) and the two linking sheets (`:728-820`), and closed the last seam, the
 * `onClick` of «Это уже наш человек» (`:480`). Nothing of the old card is absent now.
 *
 * F-017 T13 (`docs/specs/features/F-017/plan.md`, `FND-502`) brought the act of entering a person
 * without Telegram: `busy` (`:166`), the entering comment and its four states (`:167-175`,
 * `:202-205`), `enter()` with its comment (`:237-277`), the create button (`:714-726`) and its
 * sheet (`:822-868`). `enter()` calls no `onDone` in the old file either — its comment says why
 * (`:246-250`) — so the prop's absence opens no hole in it. The sheet keeps what was typed after
 * it is closed (**`FND-537`, `KEEP`**).
 *
 * `subLine` is **`rosterLine`** here (`:54`, called at `:609` in the roster and at `:768` in the
 * first linking sheet): the person card has a `subLine` of its own that says different things
 * about the same person, and neither may be importable in the other's place — **`FND-509`,
 * `KEEP`**. Those three lines are seams. The roster's part heading at their `:560` is a
 * fourth, `FND-913`'s, a finding pending on `main`.
 *
 * Carried as they stand: the comments at `:101-117` and `:127-131`, which still describe the
 * row's «…» and the part filter that are gone (**`FND-515`, `KEEP`**); the load failure's text,
 * stored and read by nothing (`:212-215`, **`FND-505`, `KEEP`**); `Состав · 0` over the
 * skeleton and the failure (`:501-503`, **`FND-514`, `KEEP`**); `pending` rows drawn nowhere
 * and nothing else filtered (`:361-362`, **`FND-504`** — `FIX` in the legacy repository, the
 * client reads the answer as is); and this card's own `GET /choir/members`, beside the hub's
 * rather than merged into it (**`FND-510`, `KEEP`**).
 *
 * Their `:3` takes the server shapes from `../api`; here they come from `@contracts/index`
 * (ADR-0011). The regions of the old file this one reproduces line for line are on
 * `scripts/ported-partial.txt`.
 */

/**
 * The line under a request: how they got here and when.
 *
 * Gender-neutral, and that is not fussiness: the choir has brothers and sisters
 * in it and nobody has asked this person anything yet, so «перешёл» would be a
 * guess printed under their name.
 */
function knockedAt(one: JoinRequest): string {
  const when = relativeDay(one.requested_at);
  return when === null ? 'по ссылке' : `по ссылке · ${when}`;
}

/**
 * The line under a name in «В паузе»: why, or when it began.
 *
 * The reason reaches whoever keeps the roster and nobody else — not the choir,
 * and not the person it is about, who hears it from the regent rather than from
 * the app. The server withholds the field, so this cannot tell «no note» from
 * «not yours to read» and does not have to: both are «без пометки», and the
 * month is what everybody else gets instead.
 */
function awayLine(row: MemberRow, canSeeReason: boolean): string {
  const part = voicePartName(row.voice_part);
  if (canSeeReason) return `${part} · ${row.pause_note ?? 'без пометки'}`;
  if (row.paused_at === null) return part;
  return `${part} · с ${monthOf(new Date(row.paused_at).getMonth() + 1)}`;
}

// `subLine` in the old file (`:54`), and `rosterLine` here: `PersonScreen.tsx:561` has a
// `subLine` of its own that says different things about one person — FND-509, `KEEP`.
/**
 * The line under a name: whatever else is true of the person.
 *
 * It began with the voice part and stopped on 2026-09-04, when the roster
 * became four sections with «СОПРАНО · 4» over each: the heading says it once
 * for four rows, and repeating it under every name was the same word four times
 * on one screenful. Where nothing else is true the tenure stands in its place,
 * so every row keeps two lines — a list where some rows are one line tall and
 * some are two reads as a list with holes in it.
 */
function rosterLine(row: MemberRow): string {
  const parts: string[] = [];
  // «старший регент» where an ordinary one is «регент»: one word apart, in the
  // same grey and with no pill, because a rank is who to go to rather than a
  // verdict on anybody — the same grammar the two functions below are written
  // in. Who may act on people is written too: it is the answer to «почему у
  // меня нет „…“ на чужой строке», and a choir that cannot see it goes to the
  // wrong person.
  if (row.is_head_regent) parts.push('старший регент');
  else if (row.role === 'regent') parts.push('регент');
  if (row.is_roster_admin) parts.push('ведёт состав');
  // The two delegated functions, in the same list of qualifiers, the same grey
  // and no pill — the way «регент» is written. Who keeps the programme is a
  // fact about the choir rather than a verdict on somebody, and the whole choir
  // reads it: a singer looking for whom to ask about Sunday's order finds the
  // name here.
  if (row.is_program_admin) parts.push('ведёт программу');
  if (row.is_sermon_admin) parts.push('ставит проповеди');
  if (row.is_me) parts.push('это ты');
  // The two-rehearsal rule, in the same list of qualifiers and in the same ink:
  // no colour and no pill, because it is a rule and not a verdict on the
  // person - the owner's «тихая пометка, без цвета тревоги». One miss is
  // written too: it is the regent who needs that, not the singer, and the row
  // does not grow for it - this line clips.
  if (row.misses_streak > 0) parts.push(missesInARow(row.misses_streak));
  // Somebody the regent entered by hand, and this is said instead of the line
  // below rather than beside it: they have not refused the bot, they have no
  // chat for it to write into. «напоминания не дойдут» would read as a switch
  // somebody flipped, and a person with no smartphone would spend the rest of
  // the year being accused of it.
  if (!row.has_telegram) parts.push('без Telegram');
  // In plain words rather than "allows_write_to_pm": otherwise the regent never
  // learns why somebody keeps turning up without a reminder. Explicitly
  // `false`, because a singer's copy of the roster carries null there - the
  // server withholds the one field that is the regent's record about somebody
  // rather than a fact about them.
  else if (row.allows_write_to_pm === false) parts.push('напоминания не дойдут');
  if (parts.length === 0) return tenureLine(row.tenure_years);
  return parts.join(' · ');
}

/** «в хоре 3 года», or the first year, which has no number to give. */
function tenureLine(years: number): string {
  if (years < 1) return 'первый год';
  return `в хоре ${years} ${plural(years, ['год', 'года', 'лет'])}`;
}

/**
 * The choir roster: appointing a regent and letting a member go.
 *
 * A second regent is the only insurance against a lost phone, and they have to
 * be appointed in advance, while the first one still can. Which is why this list
 * is the choir screen itself rather than something to open: between rehearsals
 * it is in plain sight.
 *
 * What each action does about being sure is decided by whether it can be taken
 * back, which is the rule of 2026-08-21 rather than anything about this screen.
 * The role goes both ways through the same menu, so it changes on the tap and
 * offers «Отменить» for five seconds. Bringing somebody back is the same in
 * reverse. Removing is not: it needs a fresh invitation to undo, so it is asked
 * from the sheet every irreversible thing in the app is asked from. All three
 * used to ask alike, and asking alike is what stopped the question saying
 * anything about the weight of the answer.
 */
export default function MembersCard({
  myMemberId,
  readOnly = false,
  supportContact = '',
  onOpen,
  onDone,
}: {
  /** Whose row is the reader's own, which is marked by tone and by a word. */
  myMemberId: number;
  /**
   * A singer's copy of the roster: who is in the choir, and nothing that is a
   * regent's record about them. No «…», no filter by voice part, and the invite
   * link is not on this screen at all — it moved to «Я» on 2026-08-22, which is
   * the day «Хор» stopped being the regent's tab.
   */
  readOnly?: boolean;
  /**
   * Whom to write to when the choir has lost the one person who can hand the
   * seniority on, or empty. See `support_contact` on the auth response.
   */
  supportContact?: string;
  /**
   * Somebody's row was tapped. Every row is a door since 2026-08-27, for the
   * whole choir and not only for the regent: what is behind it is the figures,
   * the year and — for whoever keeps the roster — the three things that can be
   * done to a person. The screen belongs to «Хор» rather than to this card,
   * because it replaces the whole tab rather than standing inside the list.
   */
  onOpen: (memberId: number) => void;
  onDone: Report;
}) {
  const [members, setMembers] = useState<MemberRow[] | null>(null);
  /** Who is waiting at the door. Loaded only by somebody who may answer them. */
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  /* Answered requests stay on the screen for the length of their own closing.
     The list below is drawn from this rather than from `requests`; the count in
     the label is not, because the count is what is true and this is what is on
     screen. */
  const knocking = useLeaving(requests, (one) => one.request_id);
  /** Whether «В паузе» is unfolded. Shut by default: it is not the roster. */
  const [pausedOpen, setPausedOpen] = useState(false);
  /**
   * The refusal and where it belongs: `screen` for loading, a member id for
   * anything done to one person. It was one string under the whole roster, so
   * a menu opened in the third row answered under the fourteenth - the same
   * defect the schedule had, reported on 2026-08-21 and fixed there first.
   */
  const [failure, setFailure] = useState<{ at: string | number; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Entering somebody who has no Telegram, and what is typed into that form.
   *
   * A sheet because it is filled in, which is the container rule of
   * 2026-08-21. Three fields and no fourth: a name, a part and a sex, the same
   * three the regent's copy of the profile form corrects afterwards. There is
   * nothing to ask about an account, because the whole point of the row is that
   * there is not one.
   */
  /**
   * The request being joined to somebody already in the roster, or null.
   *
   * The third answer at the door, and the rare one: a person is recognised by
   * their Telegram and by nothing else, so a singer who changed their number
   * arrives as a stranger. Accepting made a second row with the same name and
   * nothing joined them.
   */
  const [linking, setLinking] = useState<JoinRequest | null>(null);
  /**
   * The row picked in that list when it still has a way into the app of its own.
   *
   * Linking onto it takes that way away — the person is locked out on their next
   * request, and if they are the head regent nobody left can put them back. So
   * the tap opens this instead of doing it, and the name has to be typed out,
   * which is what «Удалить из хора» already asks for the same reason. A row
   * with no account has nothing to lose and is linked by the tap alone.
   */
  const [replacing, setReplacing] = useState<MemberRow | null>(null);
  const [typedLink, setTypedLink] = useState('');
  /**
   * Which request has its «…» open, or null. One at a time, the way the roster
   * opens a person's actions: two panels standing open in one list is not a
   * state anybody asked for.
   */
  const [moreAt, setMoreAt] = useState<JoinRequest['request_id'] | null>(null);
  const [entering, setEntering] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPart, setNewPart] = useState<VoicePart | null>(null);
  const [newBrother, setNewBrother] = useState<boolean | null>(null);

  const load = useCallback(async () => {
    try {
      setMembers(await api.choirMembers());
      setFailure(null);
    } catch (error) {
      setFailure({
        at: 'screen',
        text: error instanceof ApiError ? error.message : 'Не удалось загрузить состав',
      });
    }
  }, []);

  /**
   * Who has knocked. A request of its own rather than a filter over the roster,
   * because it is refused to anybody who may not answer one — and a 403 the
   * screen would have nothing to do with is not worth asking for.
   */
  const knock = useCallback(async () => {
    try {
      setRequests(await api.joinRequests());
    } catch {
      // A block that cannot be loaded is simply not drawn. Nothing else on the
      // screen depends on it, and the roster below is still true.
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Put somebody in the choir who cannot put themselves in it.
   *
   * The refusal stays inside the sheet: it does not close until the request has
   * gone through, so an answer left behind the darkening is an answer nobody
   * reads — the same rule the schedule's form and every `Ask` follow.
   *
   * It ends on the person rather than on the list, which is the schedule's own
   * precedent of 2026-08-24: somebody who has just typed a name wants the thing
   * they have just made, and the list is one tap back. It also settles what to
   * report, and the answer is nothing: `Report` always draws «Отменить», and
   * here there would be nothing behind it — taking a person out again is a
   * separate act that asks for their name typed out. A word that undoes nothing
   * is worse than no word, and the row standing on screen says it better.
   */
  async function enter(): Promise<void> {
    const name = newName.trim();
    if (name === '') return;
    setBusy(true);
    setFailure(null);
    try {
      const made = await api.createMember({
        displayName: name,
        voicePart: newPart,
        isBrother: newBrother,
      });
      setEntering(false);
      setNewName('');
      setNewPart(null);
      setNewBrother(null);
      await load();
      onOpen(made.member_id);
    } catch (error) {
      setFailure({
        at: 'enter',
        text: error instanceof ApiError ? error.message : 'Не удалось завести человека',
      });
    } finally {
      setBusy(false);
    }
  }

  /**
   * «Принять» or «Нет», with the same five seconds to take it back.
   *
   * The undo is the opposite call and there is not one — declining deletes the
   * row, and accepting the same person again is a request that no longer
   * exists. So «Отменить» re-reads instead of reversing: it is an honest report
   * that something happened over a screen that is already true, and the way
   * back from either is named in words — a person who was let in can be paused,
   * and a person who was refused knocks again on the same link.
   */
  async function answer(one: JoinRequest, take: boolean): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      if (take) await api.acceptRequest(one.request_id);
      else await api.declineRequest(one.request_id);
      // The row this menu belonged to is gone, and a panel left open would
      // reopen under whichever request takes its place in the list.
      setMoreAt(null);
      setRequests((current) => current.filter((row) => row.request_id !== one.request_id));
      if (take) await load();
      onDone(take ? `${one.display_name} в хоре` : `Заявка отклонена`, () => {
        void knock();
        void load();
      });
    } catch (error) {
      setFailure({
        at: one.request_id,
        text: error instanceof ApiError ? error.message : 'Не получилось, попробуй ещё раз',
      });
    } finally {
      setBusy(false);
    }
  }

  /**
   * Join the account at the door to the row it belongs to.
   *
   * No `onDone` bar: what it does is not reversible from a bar — putting the
   * old account back would mean the person knocking again from a phone they no
   * longer have. The sheet closes and the roster below is the answer, one row
   * shorter at the door and unchanged in the count.
   */
  async function link(one: JoinRequest, memberId: number, confirmName?: string): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      await api.linkRequest(one.request_id, memberId, confirmName ?? null);
      setLinking(null);
      setReplacing(null);
      setTypedLink('');
      setMoreAt(null);
      setRequests((current) => current.filter((row) => row.request_id !== one.request_id));
      await load();
    } catch (error) {
      setFailure({
        // Read where the tap was made: the confirmation stands over the list,
        // so a refusal left in the list behind it would be under the sheet.
        at: confirmName === undefined ? 'link' : 'replace',
        text: error instanceof ApiError ? error.message : 'Не удалось привязать',
      });
    } finally {
      setBusy(false);
    }
  }

  const all = members ?? [];
  /**
   * What this reader may do to people, read off their own row rather than told
   * by a field of its own: the head regent always, a second regent only while
   * the head trusts him with the roster. A regent without it runs the
   * rehearsals and sees the roster with the regent's own note on it — he simply
   * has no «…» on anybody's row, which is what a singer's copy already looks
   * like and is the reason no fifth state had to be invented for it.
   */
  const me = all.find((row) => row.member_id === myMemberId) ?? null;
  const isHead = me?.is_head_regent === true;
  const canManage = !readOnly && me?.role === 'regent' && (isHead || me.is_roster_admin);
  /* The three states, each drawn in its own place and nowhere else: the choir,
     the people in a pause under it, and the requests over both. Everything on
     this screen that counts people counts the first of the three — «Состав · 6»
     is the choir, and somebody in a pause is not in it. */
  const active = all.filter((row) => row.standing === 'active');
  const away = all.filter((row) => row.standing === 'paused');
  /* Four sections and a fifth only where somebody is in it. The four are the
     parts, in the order the app always names them; the fifth is «ждут партию»,
     which is not a part but a state - a member who joined by a link has none
     until the regent gives one, and dropping those rows would hide people from
     the list whose whole job is that nobody is missing from it. It was a chip
     beside the filters until they went. */
  const sections: { title: string; rows: MemberRow[] }[] = [
    ...PARTS.map((part) => ({
      title: voicePartName(part),
      rows: active.filter((row) => row.voice_part === part),
    })),
    { title: 'ждут партию', rows: active.filter((row) => row.voice_part === null) },
  ].filter((section) => section.rows.length > 0);
  /* The list's own liveness: every face on its own clock, and one scheduler
     over the whole roster picking a single visible face every 6-14 seconds.
     One scheduler and not one per row, because the rule is «never two at
     once» and forty independent timers cannot keep it. Somebody in a pause is
     out of the pool by `away`: their face has no mouth to move. */
  const faces: LiveFace[] = active.map((row) => ({
    id: row.member_id,
    smile: smileOf(row.attendance_rate_30d),
    away: false,
  }));
  const { seen, gesture } = useGestures(faces);

  // Asked once the roster has said whether this reader may answer a request:
  // a singer's copy of this screen has no block for them, and asking for a
  // list that is refused would be a 403 the screen has nothing to do with.
  useEffect(() => {
    if (!canManage) return;
    void knock();
  }, [canManage, knock]);

  // The comment below is ported as it stands, and it says less than the card does: «none of the
  // rest» is not true of the second-regent hint, which is gated by `canManage` alone and stands
  // under the empty card for whoever keeps the roster — FND-516, `KEEP`.
  /* A choir of one is the state everybody who creates a choir lands in, and it
     is the one this screen never drew: a section label, a row of filters over
     nobody, and the regent's own row saying nothing they do not know. It gets
     the empty card and none of the rest — but only while nothing else is on the
     screen: a request or a person in a pause is something to act on. */
  const alone = members !== null && all.length <= 1 && requests.length === 0;

  return (
    <div className="block block--roster">
      {/* Over the roster, and only for somebody who can answer one. A request
          is not a member and is not drawn as one: no avatar, because nobody has
          given this person a part and there is nothing to draw a figure out of.

          Both answers are reversible in the sense the rule means — «Нет» drops
          a row that costs one more tap on the same link to recreate — so
          neither is asked about: the tap does it and the bar offers «Отменить»
          for five seconds, the same as the roles. */}
      {/* The block stands while there is a row to draw and the count says what
          is true, so the last request answered leaves «Заявки · 0» over itself
          for a fifth of a second. That is what happened: there are none left,
          and the last one is still going. */}
      {/* C-023 batch 2 (`FND-1618`): the requests come in after the roster under them has
          been drawn, so arriving whole they pushed it down in one frame (176 px on the audit's
          rig). They grow in over it instead, and fold away when the last one is answered. */}
      <Unfold open={canManage && knocking.rows.length > 0}>
        <div className="knock-fold">
          <span className="label">
            Заявки · <span className="section-count">{requests.length}</span>
          </span>
          <ul className="live live--roster">
            {knocking.rows.map((one) => (
              <li
                className={[
                  'request-item',
                  moreAt === one.request_id ? 'request-item--menu' : null,
                  // Accepted or refused, and either way this row is finished
                  // with. It closes rather than vanishing, so the requests under
                  // it do not jump up into the place the finger is still on.
                  knocking.leaving(one) ? 'row--going' : null,
                ]
                  .filter((name) => name !== null)
                  .join(' ')}
                key={one.request_id}
              >
                <span className="request">
                  <span className="request-body">
                    <span className="live-name">{one.display_name}</span>
                    <span className="live-sub">{knockedAt(one)}</span>
                  </span>
                  <button
                    className="btn btn--primary"
                    disabled={busy}
                    onClick={() => void answer(one, true)}
                  >
                    Принять
                  </button>
                  <button
                    className="btn btn--quiet"
                    disabled={busy}
                    onClick={() => void answer(one, false)}
                  >
                    Нет
                  </button>
                  {/* A handle and not a third offer: the same «…» a person in
                      the roster carries, for the same reason — fifty requests
                      each with a button under them is a wall, and «Принять» is
                      what nine of every ten of them are. */}
                  <button
                    className="live-dots"
                    data-press="none"
                    aria-label={`Что ещё можно сделать с заявкой «${one.display_name}»`}
                    aria-expanded={moreAt === one.request_id}
                    onClick={() => setMoreAt(moreAt === one.request_id ? null : one.request_id)}
                  >
                    <DotsIcon size={20} />
                  </button>
                </span>
                {/* The third answer, and it is the rare one. A person is
                    recognised by their Telegram and by nothing else, so a
                    singer who changed their number knocks as a stranger — and
                    accepting made a second row with the same name that every
                    closing then wrote an absence for. */}
                <Unfold open={moreAt === one.request_id}>
                  <div className="menu">
                    <button
                      className="menu-action"
                      disabled={busy}
                      onClick={() => setLinking(one)}
                    >
                      Это уже наш человек
                    </button>
                  </div>
                </Unfold>
                {/* Under the row the tap was in, not under the whole block:
                    the rule this screen has followed since 2026-08-21. */}
                {failure !== null && failure.at === one.request_id && (
                  <p className="failure">{failure.text}</p>
                )}
              </li>
            ))}
          </ul>
          <p className="hint">
            Заявка по ссылке. Пока не примешь, хор не открыт.
          </p>
        </div>
      </Unfold>

      {/* The whole choir, and the four sections under it carry the parts. */}
      <span className="label">
        Состав · <span className="section-count">{active.length}</span>
      </span>

      {/* Nothing yet, and the two states that are not the list. A reload that
          fails over a roster already drawn draws neither: the roster stands. */}
      {members === null && failure?.at !== 'screen' && <Skeleton />}
      {members === null && failure?.at === 'screen' && (
        <Failed subject="members" onRetry={() => void load()} />
      )}

      {alone && (
        <Settling>
          <Empty
            fact="В хоре пока только ты"
            note="Отправь ссылку-приглашение — вошедшие появятся в этом списке."
          />
        </Settling>
      )}

      <Settling>
        {!alone && canManage && isHead && (
          <p className="hint">
            Ты старший регент. Тебя нельзя разжаловать или убрать из хора — иначе хор остался бы без
            человека, который может всё вернуть. Уйти со старшинства можно, только передав его
            другому регенту.
          </p>
        )}
        {!alone && !readOnly && !canManage && (
          <p className="hint">
            Состав хора ведёт старший регент: назначить регента, поставить на паузу, поручить
            функцию и исправить чужие имя и партию может только старший. Спевки, участия,
            программа и рассылки — твои наравне со старшим.
            {/* The one place the app used to stop. Старшинство отдаёт только сам
                старший — an invariant, not an oversight — so a choir whose head
                has gone with his phone has no way forward inside the app at all,
                and until now was not told there was one outside it. Printed only
                where somebody has actually hit the wall, and only where the
                installation has said whom to ask: empty, this sentence does not
                exist and the hint reads exactly as it did. */}
            {supportContact !== '' && (
              <>
                {' '}
                Если старшего больше нет — писать @{supportContact}: ранг переносят руками.
              </>
            )}
          </p>
        )}

        {/* Four sections and a heading over each, since 2026-09-04. The face
            lost the shape that used to say the part, and this is where the part
            went: a heading counts the section it names, where a silhouette had
            to be learnt before it counted anything, and it does so for the
            singer as well as for the regent — the filter chips that answered
            the same question were the regent's alone. */}
        {!alone && (
          <div className="roster-parts">
            {sections.map((section) => (
              <div className="roster-part" key={section.title}>
                <span className="label label--sub">
                  {section.title} · <span className="section-count">{section.rows.length}</span>
                </span>
                <ul className="live live--roster">
                  {section.rows.map((row) => (
                    <li
                      key={row.member_id}
                      ref={seen(row.member_id)}
                      className={
                        row.member_id === myMemberId
                          ? 'live-row live-row--person live-row--mine'
                          : 'live-row live-row--person'
                      }
                    >
                      {/* One target in the row and no second one. The «…» stood
                          here with everything a regent does to a person behind
                          it, and the chevron arrived beside it on 2026-08-27 —
                          two entrances to the same acts inside 46px, neither of
                          them saying how it differed from the other. The menu
                          went; what was in it is on the person's own screen,
                          where there is room for the name, the buttons and a
                          line saying what each of them costs. */}
                      <span className="live-head">
                        <button
                          className="live-tap"
                          data-press="fade"
                          onClick={() => onOpen(row.member_id)}
                        >
                          {/* No status between rehearsals, so the ring is the
                              quiet outline — same grammar as an unmarked roster
                              row. The face is alive: its own phase for the idle
                              step it is on, and the list's scheduler picking it
                              out once in a while for a gesture. Remounted by a
                              key made of the moment, because an animation does
                              not restart when its class is put back. */}
                          <Avatar
                            key={
                              gesture?.id === row.member_id ? `${row.member_id}:${gesture.at}` : row.member_id
                            }
                            eyes={row.avatar_eyes}
                            color={row.avatar_color}
                            rate={row.attendance_rate_30d}
                            size={AVATAR_ROW}
                            phase={livenessPhase(row.member_id, smileOf(row.attendance_rate_30d))}
                            move={gesture?.id === row.member_id ? gesture.move : null}
                            mine={row.is_me}
                          />
                          <span className="live-body">
                            <span className="live-name">{row.display_name}</span>
                            <span className="live-sub">{rosterLine(row)}</span>
                          </span>
                          {/* The share, monospaced and tabular: a column of
                              percentages that shifts by a pixel when one of them
                              changes is a column nobody can read down. Absent
                              where the window holds no rehearsals — null is not
                              nought. */}
                          {row.attendance_rate_30d !== null && (
                            <span className="live-rate">
                              {Math.round(row.attendance_rate_30d * 100)}%
                            </span>
                          )}
                          <ChevronRightIcon size={20} quiet />
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {/* Only where a row actually carries the mark. The line used to stand
            under any roster of more than one, so the commonest way to read it was
            as an explanation of a phrase that was nowhere on the screen - and it
            explained it in the app's own shorthand at that: «счёт обнулит
            прошедшее участие» reads as the word «участие» that is a kind of
            gathering, not as the sentence about a service that has happened. */}
        {all.some((row) => row.misses_streak > 0) && (
          <p className="hint">
            «Две спевки подряд» в строке — это пропуски и это правило, а не оценка человека: с двух
            подряд на участие не идут. Уважительный пропуск в счёт не идёт, счёт обнуляет посещённая
            спевка, а прошедшее участие обнуляет его всему хору.
          </p>
        )}
        {canManage && (
          <p className="hint">
            Назначь второго регента заранее. Если телефон единственного регента потеряется,
            открыть спевку будет некому. Людей второй регент не трогает, пока старший не доверит
            состав.
          </p>
        )}

        {/* Under the roster and folded shut, because it is not the choir: what
            the section holds is people the app is closed to and the bot does not
            write to. The word and the count are the whole heading — a section
            that opens on a tap says what is in it before the tap.

            The rows are the roster's rows with three things taken off: the face
            loses its colour and its mouth, the share goes, and so does the «…».
            The share, because thirty days of somebody who was not allowed at a
            rehearsal is not a figure about them; the menu, because everything it
            held is on their own screen now, one tap further in. */}
        {away.length > 0 && (
          <>
            <button
              className="fold-label"
              data-press="fade"
              aria-expanded={pausedOpen}
              onClick={() => setPausedOpen(!pausedOpen)}
            >
              <span className="label">
                В паузе · <span className="section-count">{away.length}</span>
              </span>
              <ChevronDownIcon size={16} quiet turned={pausedOpen} />
            </button>
            <Unfold open={pausedOpen}>
              <ul className="live live--roster">
                {away.map((row) => (
                  <li className="live-row live-row--person live-row--away" key={row.member_id}>
                    <span className="live-head">
                      <button
                        className="live-tap"
                        data-press="fade"
                        onClick={() => onOpen(row.member_id)}
                      >
                        <Avatar
                          eyes={row.avatar_eyes}
                          color={row.avatar_color}
                          rate={row.attendance_rate_30d}
                          size={AVATAR_ROW}
                          away
                          mine={row.is_me}
                        />
                        <span className="live-body">
                          <span className="live-name">{row.display_name}</span>
                          <span className="live-sub">{awayLine(row, canManage)}</span>
                        </span>
                        <ChevronRightIcon size={20} quiet />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="hint">
                {canManage
                  ? 'Пауза без срока: держится, пока её не снимут. Приложение у этих людей закрыто, бот им не пишет, в статистике их нет.'
                  : 'Эти люди сейчас не в составе. Вернуть их может регент.'}
              </p>
            </Unfold>
          </>
        )}
      </Settling>

      {/* The way in for somebody who cannot come in by themselves, and it is
          last on the card on purpose: the ordinary path is the link, and this
          is the exception it does not cover. Outlined rather than filled —
          nothing here is what the screen is for, and nothing here is dangerous
          either.

          Drawn for whoever keeps the roster and for nobody else, which is the
          same permission that answers a request at the door. */}
      {canManage && (
        <button className="btn btn--outline" disabled={busy} onClick={() => setEntering(true)}>
          Завести человека без Telegram
        </button>
      )}

      {/* Whose row this account belongs to. A list and not a field: the regent
          is choosing among people he knows by name, and typing one out is how
          the wrong «Анна Иванова» gets picked. Paused people are in it too —
          somebody who was away and came back with a new phone is the same
          correction. */}
      <Sheet
        open={linking !== null}
        label="Кто это из состава?"
        onClose={() => setLinking(null)}
      >
        <p className="hint">
          Аккаунт перейдёт на выбранную строку. Отметки, годы в хоре, проценты и места в
          прошлых программах останутся её — второй строки не появится, состав не вырастет,
          ссылку хора крутить не нужно. Прежний вход этого человека перестанет работать.
        </p>
        <ul className="live live--roster">
          {[...active, ...away].map((row) => (
            <li className="live-row live-row--person" key={row.member_id}>
              <button
                className="live-tap"
                data-press="fade"
                disabled={busy}
                /* A row with an account of its own asks before it loses it;
                   one entered by hand has nothing to lose and goes on the tap. */
                onClick={() =>
                  linking !== null &&
                  (row.has_telegram ? setReplacing(row) : void link(linking, row.member_id))
                }
              >
                <Avatar
                  eyes={row.avatar_eyes}
                  color={row.avatar_color}
                  rate={row.attendance_rate_30d}
                  size={AVATAR_ROW}
                  away={row.standing === 'paused'}
                  mine={row.is_me}
                />
                <span className="live-body">
                  <span className="live-name">{row.display_name}</span>
                  <span className="live-sub">
                    {row.standing === 'paused' ? 'в паузе' : rosterLine(row)}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
        {failure !== null && failure.at === 'link' && <p className="failure">{failure.text}</p>}
      </Sheet>

      {/* The second door, and only for a row that still has a way in. The list
          above is forty names one tap deep, and the head regent is a row in it
          like anybody else: landing on his by mistake used to take the
          seniority out of the choir, which only its holder can hand on. So the
          name, the same confirmation «Удалить из хора» takes — «точно?» checks
          nothing, and a name checks that this person was meant. */}
      <Sheet
        open={replacing !== null}
        label="У этого человека уже есть вход"
        onClose={() => {
          setReplacing(null);
          setTypedLink('');
        }}
        foot={
          <>
            {failure !== null && failure.at === 'replace' && (
              <p className="failure">{failure.text}</p>
            )}
            <button
              className="btn btn--danger"
              disabled={busy || typedLink.trim() !== replacing?.display_name}
              onClick={() =>
                linking !== null &&
                replacing !== null &&
                void link(linking, replacing.member_id, typedLink.trim())
              }
            >
              Перенести аккаунт
            </button>
          </>
        }
      >
        <p className="card-title">{replacing?.display_name}</p>
        <p className="hint">
          Этот человек уже заходит в приложение. Его вход перестанет работать сразу, а войти
          вместо него будет тот, кто сейчас стоит у двери. Вернуть прежний вход сможет только он
          сам — с того телефона, которого у него может уже не быть.
        </p>
        <label className="field">
          <span>Впиши имя целиком, чтобы подтвердить</span>
          <input value={typedLink} onChange={(event) => setTypedLink(event.target.value)} />
        </label>
      </Sheet>

      <Sheet
        open={entering}
        label="Человек без Telegram"
        onClose={() => setEntering(false)}
        foot={
          <>
            {failure !== null && failure.at === 'enter' && (
              <p className="failure">{failure.text}</p>
            )}
            <button
              className="btn btn--primary"
              disabled={busy || newName.trim() === ''}
              onClick={() => void enter()}
            >
              Завести
            </button>
          </>
        }
      >
        <p className="hint">
          Для тех, у кого нет смартфона или аккаунта. Такой человек считается в составе, в
          проценте и в матрице наравне со всеми, отмечает его регент — сам он в приложение не
          заходит, и бот ему не пишет.
        </p>
        <label className="field">
          <span>Имя</span>
          <input
            value={newName}
            maxLength={100}
            onChange={(event) => setNewName(event.target.value)}
          />
        </label>
        <div className="field">
          <span>Партия</span>
          <PartSegments value={newPart} busy={busy} onPick={setNewPart} />
        </div>
        {/* «Пол», the word the profile form uses since 2026-08-24: «брат» and
            «сестра» are what the choir calls each other and the wrong thing to
            ask on a form. Asked here at all for the same reason it is asked at
            the door — it decides who stands in the queue of sermons, and a
            queue that skips somebody because nobody filled a field in is a
            queue nobody trusts. */}
        <div className="field">
          <span>Пол</span>
          <SexSegments value={newBrother} busy={busy} onPick={setNewBrother} />
        </div>
      </Sheet>
    </div>
  );
}
